/* eslint-disable @typescript-eslint/no-explicit-any */
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Address } from '@vsolv/core/address/domain';
import { LoggerService } from '@vsolv/core/logging/web';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { FirebaseService } from '@vsolv/packages/firebase/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Customer } from '@wsphere/customers/domain';
import { CustomerService } from '@wsphere/customers/web';
import { Distributor } from '@wsphere/distributors/domain';
import { Provisioning } from '@wsphere/warranties/domain';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CheckoutCustomerExistsSignInDialog } from '../../dialogs';
import { ProvisioningSessionService } from '../../services';
import { ProvisioningCustomerPickerComponent } from '../customer-picker/customer-picker.component';

interface CustomerInfo {
  name: string;
  email: string;
  phone: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-provision-customer-info',
  templateUrl: './provision-customer-info.component.html',
  standalone: false,
})
export class ProvisionCustomerInfoComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private firebaseSvc: FirebaseService,
    private customerSvc: CustomerService,
    private sessionSvc: ProvisioningSessionService,
    private breakpointObserver: BreakpointObserver,
    private logger: LoggerService
  ) {}

  @HostBinding('class') _class = 'pb-24 px-6 w-full flex flex-col items-center';

  @ViewChild('customerExistsDialog') customerExistsDialog!: CheckoutCustomerExistsSignInDialog;
  @ViewChild('confirmSwapDialog') confirmSwapDialog?: DialogComponent;
  @ViewChild(ProvisioningCustomerPickerComponent) customerPicker?: ProvisioningCustomerPickerComponent;

  /** The customer the user is trying to swap to — held while confirmation is pending. */
  protected pendingCustomerSwap: Customer.Model | null = null;

  private destroy$ = new Subject<void>();
  private checkedCustomerExistsEmail: string | null = null;

  @PropertyListener('queryParams') queryParams$ = new BehaviorSubject<ParamMap | undefined>(undefined);
  queryParams!: ParamMap;

  readonly staffView$ = this.route.data.pipe(map(data => data['staffView'] as boolean));

  protected showFlow = true;
  protected isProcessing = false;

  protected dataCustomer: Customer.Model | null = null;

  protected session$: Observable<Provisioning.ProvisioningSession | null> = combineLatest([
    this.sessionSvc.getCurrentSession(),
    this.sessionSvc.data$.asObservable(),
    this.staffView$,
  ]).pipe(
    tap(async ([session, data, staffView]) => {
      this.dataCustomer = session?.customer || data.customer || null;
      this.customer.patchValue(session?.customer || data.customer || null);
      this.customerInfo.patchValue({
        name: session?.customer?.name || data.customerInfo?.name || this.queryParams?.get('name') || '',
        email: session?.customer?.email || data.customerInfo?.email || this.queryParams?.get('email') || '',
        phone: session?.customer?.phone || data.customerInfo?.phone || this.queryParams?.get('phone') || null,
      });
      this.addressControl.setValue(
        session?.customer?.address ??
          data.customer?.address ??
          (this.queryParams?.get('line1')
            ? ({
                line1: this.queryParams?.get('line1'),
                line2: this.queryParams?.get('line2'),
                zip: this.queryParams?.get('zip'),
                city: this.queryParams?.get('city'),
                state: this.queryParams?.get('state'),
                country: this.queryParams?.get('country'),
              } as Address.Model)
            : null)
      );
      this.distributorId = data.distributorId || undefined;
      this.distributorData = data.distributor;
      this.viewablePlansAndTerms = data.viewablePlansAndTerms;
      this.expiryDate = data.expiryDate;
      if (this.customer.valid && this.customerInfo.valid) this.stepCompleted = true;

      const email = this.queryParams?.get('email');
      this.emailControl.setValue(data?.customer?.email || email);
      if (!staffView && !this.customerInfo.valid && !session && !email) this.showFlow = false;
      else this.showFlow = true;

      // This stream can emit several times during init; avoid re-checking the same email repeatedly.
      if (!data.customer && email && this.checkedCustomerExistsEmail !== email) {
        this.checkedCustomerExistsEmail = email;
        await this.redirectIfExists(email);
      }
    }),
    switchMap(async ([session, _]) => session)
  );

  protected distributor$ = this.sessionSvc.distributor$.asObservable();

  protected emailControl = new FormControl('', [Validators.required, Validators.email]);
  protected customerInfo = new FormControl(null as CustomerInfo | null);
  protected addressControl = new FormControl(null as Address.Model | null, [Validators.required]);
  protected customer = new FormControl(null as Customer.Model | null);

  stepCompleted = false;

  distributorId?: string;
  distributorData?: any;

  viewablePlansAndTerms?: Provisioning.ViewablePlansAndTerms;
  expiryDate?: Date;

  isLargeScreen$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => !state.matches));

  customerSelected(customer: Customer.Model, session: Provisioning.ProvisioningSession | null) {
    const isSwap = !!(session?.customerId && session.customerId !== customer.id);
    if (isSwap) {
      // If the user has progressed past the Customer step, swapping would silently
      // wipe their downstream selections. Surface a confirmation dialog and let
      // them decide. Without downstream work, the swap is harmless — do it immediately.
      const hasDownstreamWork = !!(session?.policyId || session?.planId || session?.assetId);
      if (hasDownstreamWork) {
        this.pendingCustomerSwap = customer;
        this.confirmSwapDialog?.open();
        return;
      }
      this.applyCustomerSwap(customer);
      return;
    }
    this.applyCustomerPatch(customer);
  }

  /** Confirm the swap from the dialog. Called by the "Switch customer" button. */
  protected confirmCustomerSwap() {
    if (!this.pendingCustomerSwap) return;
    this.applyCustomerSwap(this.pendingCustomerSwap);
    this.pendingCustomerSwap = null;
    this.confirmSwapDialog?.close();
  }

  /** Cancel the swap from the dialog. Called by the "Keep current" button. */
  protected cancelCustomerSwap() {
    // The picker self-mutates its internal value on dropdown click and is
    // OnPush — the parent's [value] binding doesn't push back unless the
    // expression changes reference. Use the picker's resetValue() so it both
    // restores the display AND marks itself for check.
    this.customerPicker?.resetValue(this.dataCustomer ?? this.customer.value ?? null);
    this.pendingCustomerSwap = null;
    this.confirmSwapDialog?.close();
  }

  /**
   * Apply a customer swap: clear the local session so the next Continue creates
   * a fresh one bound to this customer. Also update data$ before clearing the
   * session — the session$ tap is async (awaits queryParamMap), so after
   * clearSession it resumes with session=null and re-patches the form from
   * data$. Without updating data$ first it would overwrite our new patches.
   */
  private applyCustomerSwap(customer: Customer.Model) {
    this.sessionSvc.data$.next({
      ...this.sessionSvc.data$.value,
      customer,
      customerInfo: { name: customer.name, email: customer.email, phone: customer.phone },
    });
    this.sessionSvc.clearSession();
    this.toastSvc.show({
      type: 'info',
      title: 'Switched customer',
      text: `Starting a fresh provision for ${customer.name}.`,
    });
    this.applyCustomerPatch(customer);
  }

  private applyCustomerPatch(customer: Customer.Model) {
    this.customer.patchValue(customer);
    this.customerInfo.patchValue({
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
    });
    this.addressControl.setValue(customer.address);
    this.stepCompleted = this.validateCustomerStep();
  }

  private validateCustomerStep() {
    const phone: string | null = this.customerInfo.value?.phone || null;
    if (phone) {
      const valid = isValidPhoneNumber(phone, 'US');
      if (!valid) return false;
    }
    return this.customerInfo.valid && this.addressControl.valid;
  }

  previousPage(staffView: boolean) {
    if (staffView) this.navigateTo('/provisioning');
    else this.navigateTo('/checkout');
  }

  async nextPage(
    session: Provisioning.ProvisioningSession | null,
    staffView: boolean,
    distributor: Distributor.Model | null
  ) {
    if (this.stepCompleted) {
      this.isProcessing = true;
      if (!session) {
        await this.createSession(session, staffView, distributor);
      } else {
        const distributorId = distributor?.id ?? session.distributorId ?? this.distributorId ?? null;
        if (this.customerInfo.value)
          await this.sessionSvc.updateSession({
            sessionId: session.id,
            data: {
              customerInfo: this.customerInfo.value,
              distributorId,
            },
          });
      }
      this.isProcessing = false;
      if (staffView) {
        this.navigateTo('/provisioning/policy');
      } else this.navigateTo('/checkout/policy');
    }
  }

  private navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }

  async next() {
    const email = this.emailControl.value;
    if (!email) return;

    if (this.dataCustomer && email !== this.dataCustomer.email) {
      throw new Error('Email is invalid');
    }
    const exists = await this.redirectIfExists(email);
    if (exists) return;
    this.customerInfo.patchValue({
      email: email || '',
      name: '',
      phone: null,
    });
    this.addressControl.setValue(null);
    this.showFlow = true;
    this.stepCompleted = false;
  }

  async redirectIfExists(email: string) {
    if (!this.dataCustomer) {
      const exists = await this.customerSvc.checkIfCustomerExists(email);
      if (exists) {
        let redirect = 'checkout';
        const keys = this.queryParams.keys;
        for (let index = 0; index < keys.length; index++) {
          if (index === 0) redirect += '?';
          const key = keys[index];
          const params = this.queryParams.getAll(key).join(`&${key}=`);
          redirect += `${key}=${params}`;
          if (index < keys.length - 1) redirect += '&';
        }
        await this.router.navigate(['login'], { queryParams: { email, autosend: true, redirect } });
        return true;
      }
    }
    return false;
  }

  private async createSession(
    session: Provisioning.ProvisioningSession | null,
    staffView: boolean,
    distributor: Distributor.Model | null
  ) {
    const values = this.customerInfo.value;
    const address = this.addressControl.value;
    let email: string | null = values?.email || this.customer.value?.email || null;
    if (!staffView) {
      const authUser = await firstValueFrom(this.firebaseSvc.authUser$);
      if (authUser) {
        email = authUser.email;
      }
    }

    if (session || !values || !values.name || !email || !address?.line1) return;
    try {
      const session = await this.sessionSvc.create({
        distributorId: this.distributorId || distributor?.id,
        distributor: this.distributorData,
        customerId: this.customer.value?.id,
        customerName: values.name,
        customerEmail: email,
        customerPhoneNumber: values.phone,
        customerAddress: address,
        viewablePlansAndTerms: this.viewablePlansAndTerms,
        expiryDate: this.expiryDate,
      });
      this.sessionSvc.refreshSession(session);
      // FIXME: Provisioning session local storage
      // if (!staffView) localStorage.setItem('provisioning-session', session.id);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const status = err?.status || err?.response?.status;
      if (err?.error?.message?.[0]?.includes('must be an email')) {
        this.toastSvc.show({
          type: 'error',
          title: 'Invalid email address',
          text: `Sorry, looks like your email address isn't formatted correctly! Please try again.`,
        });
      } else if (status === 409) {
        // Use structured error code for conflict reason
        const errorCode = err?.error?.code;
        const conflictMsg = err?.error?.message || '';
        if (errorCode === 'DUPLICATE_EMAIL') {
          this.toastSvc.show({
            type: 'error',
            title: 'This email address is already in use',
            text: `Please try a different email address ${
              !staffView ? ' or signing in!' : 'or select the matching customer!'
            }`,
          });
          if (!staffView) {
            //open sign in dialog
            this.customerExistsDialog?.open(email);
          }
        } else {
          this.toastSvc.show({
            type: 'error',
            title: 'Conflict',
            text: conflictMsg || 'A conflict occurred. Please check your input and try again.',
          });
        }
      } else {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong!',
          text: err?.error?.message || 'Unknown error',
        });
      }
      this.logger.error('Failed to create provisioning session', err);
      this.isProcessing = false;
      throw new Error();
    }
  }

  ngOnInit(): void {
    this.queryParams = this.route.snapshot.queryParamMap;

    combineLatest([this.customerInfo.valueChanges, this.addressControl.valueChanges])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.stepCompleted = this.validateCustomerStep()));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
